import { Column, Grid, Link } from '@carbon/react'
import { Certificate, Checkmark, Globe } from '@carbon/react/icons'
import type { KmerLocale } from '@kmerhosting/i18n'
import { homeCopy, type HomeCopy } from '@/lib/home-i18n'

function isExternalHref(href: string) {
  return /^https?:\/\//.test(href)
}

function localizedHref(href: string, locale: KmerLocale) {
  if (!href.startsWith('/') || href.startsWith('//') || locale === 'en') return href
  return `/${locale}${href}`
}

export function TrustStandardsStrip({ copy = homeCopy('en'), locale = 'en' }: { copy?: HomeCopy; locale?: KmerLocale } = {}) {
  const trustItems = [
    {
      title: copy.privacyControls,
      label: copy.policyScope,
      description: copy.privacyDescription,
      href: localizedHref('/privacy', locale),
      icon: Checkmark,
    },
    {
      title: 'SOC 2 Type II',
      label: copy.supabaseSecurity,
      description: copy.supabaseDescription,
      href: 'https://supabase.com/docs/guides/security/soc-2-compliance',
      icon: Certificate,
    },
    {
      title: copy.domainsRegistrar,
      label: 'Atak Domain · IANA 1601',
      description: copy.icannDescription,
      href: 'https://www.icann.org/en/contracted-parties/accredited-registrars/list-of-accredited-registrars',
      icon: Globe,
    },
  ] as const

  return (
    <section className="trust-standards" aria-labelledby="trust-standards-title">
      <Grid>
        <Column sm={4} md={8} lg={4}>
          <p className="section-label">{copy.providerScope}</p>
          <h2 id="trust-standards-title" className="trust-standards__title">{copy.providerTitle}</h2>
          <p className="trust-standards__intro">{copy.providerIntro}</p>
        </Column>
        <Column sm={4} md={8} lg={12}>
          <div className="trust-standards__grid">
            {trustItems.map(({ title, label, description, href, icon: Icon }) => {
              const external = isExternalHref(href)
              return (
                <Link
                  key={title}
                  className="trust-standards__item"
                  href={href}
                  target={external ? '_blank' : undefined}
                  rel={external ? 'noreferrer' : undefined}
                >
                  <span className="trust-standards__mark" aria-hidden="true"><Icon size={16} /></span>
                  <span className="trust-standards__copy">
                    <strong>{title}</strong>
                    <span className="trust-standards__meta">
                      <span>{label}</span>
                      <span aria-hidden="true"> · </span>
                      <span>{description}</span>
                    </span>
                  </span>
                </Link>
              )
            })}
          </div>
        </Column>
      </Grid>
    </section>
  )
}
